<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-analytics-stats-breakdown">
    <div class="breakdown-header">
      <span class="over-all-content"
        >Total sessions ({{ formattedData['totalSessions'] }}) - Inactive sessions ({{ formattedData['totalInactiveSessions'] }}) = Total Conversations ({{
          formattedData['totalConversations']
        }})</span
      >
      <span class="filter-label"> </span>
      <span class="filter-label"></span>
      <el-select
        v-model="controller.selectedStat"
        ref="agentHandlingDropdown"
        collapse-tags
        popper-class="overall-breakdown"
        :style="{ width: controller.selectedStat == 'ValidReasons' ? '280px' : '215px' }"
      >
        <el-option v-for="(statTitle, statName) in statsTitles" :key="statName" :label="statTitle" :value="statName"> </el-option>
      </el-select>
    </div>
    <div class="breakdown-body">
      <div v-if="controller.selectedStat == 'conversations'" class="breakdown-box breakdown-box-all-conversation">
        <div class="header">
          <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalConversations') }">Total Conversations</div>
          <el-tooltip
            effect="dark"
            :class="{ disabled: !hasBreakDownData('totalConversations') }"
            popper-class="wy-page-tooltip-popup"
            placement="bottom"
            :disabled="!hasBreakDownData('totalConversations')"
          >
            <div slot="content"><span v-html="toolTipsContents.totalConversation"></span></div>
            <img class="info-icon" src="../../assets/icons/icon_info.svg" />
          </el-tooltip>
        </div>
        <div class="content">
          <div v-if="hasBreakDownData('totalConversations')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalConversations'] }}</span>
              <span v-if="summaryData['totalConversationsPercent'] > 99 && summaryData['totalConversationsPercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalConversationsPercent'] > 0 && summaryData['totalConversationsPercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalConversationsPercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalConversationsCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalConversationsCompare'] != 0"
                :class="{ 'positive-change-style arrow-up': summaryData['totalConversationsCompare'] > 0, 'negative-change-style arrow-down': summaryData['totalConversationsCompare'] < 0 }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalConversationsCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description">{{ formattedData['totalTopics'] }} conversation topics identified. <span class="view-topics" @click="handleOpenContactReasons">View topics</span></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
      </div>
      <div v-else class="breakdown-box breakdown-box-all-conversation">
        <div class="header">
          <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalConversationsWithATopic') }">Total Conversations with a topic</div>
          <el-tooltip
            effect="dark"
            :class="{ disabled: !hasBreakDownData('totalConversationsWithATopic') }"
            :disabled="!hasBreakDownData('totalConversationsWithATopic')"
            popper-class="wy-page-tooltip-popup"
            placement="bottom"
          >
            <div slot="content"><span v-html="toolTipsContents.totalConversationsWithAConversationTopic"></span></div>
            <img class="info-icon" src="../../assets/icons/icon_info.svg" />
          </el-tooltip>
        </div>
        <div class="content">
          <div v-if="hasBreakDownData('totalConversationsWithATopic')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalConversationsWithATopic'] }}</span>
              <span v-if="summaryData['totalConversationsPercent'] > 99 && summaryData['totalConversationsPercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalConversationsPercent'] > 0 && summaryData['totalConversationsPercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalConversationsPercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalConversationsCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalConversationsCompare'] != 0"
                :class="{ 'positive-change-style arrow-up': summaryData['totalConversationsCompare'] > 0, 'negative-change-style arrow-down': summaryData['totalConversationsCompare'] < 0 }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalConversationsCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description">{{ formattedData['totalTopics'] }} conversation topics identified. <span class="view-topics" @click="handleOpenContactReasons">View topics</span></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
      </div>
      <div class="breakdown-va-only-breakdown">
        <div class="breakdown-box breakdown-subbox-top va-originated-conversations">
          <div class="header">
            <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalVAOriginatedConversations') }">VA Originated Conversations</div>
            <el-tooltip
              effect="dark"
              :class="{ disabled: !hasBreakDownData('totalVAOriginatedConversations') }"
              :disabled="!hasBreakDownData('totalVAOriginatedConversations')"
              popper-class="wy-page-tooltip-popup"
              placement="bottom"
            >
              <div slot="content"><span v-html="toolTipsContents.totalVAOriginatedConversations"></span></div>
              <img class="info-icon" src="../../assets/icons/icon_info.svg" />
            </el-tooltip>
          </div>
          <div v-if="hasBreakDownData('totalVAOriginatedConversations')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalVAOriginatedConversations'] }}</span>
              <span v-if="summaryData['totalVAOriginatedConversationsPercent'] > 99 && summaryData['totalVAOriginatedConversationsPercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalVAOriginatedConversationsPercent'] > 0 && summaryData['totalVAOriginatedConversationsPercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalVAOriginatedConversationsPercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalVAOriginatedConversationsCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalVAOriginatedConversationsCompare'] != 0"
                :class="{
                  'positive-change-style arrow-up': summaryData['totalVAOriginatedConversationsCompare'] > 0,
                  'negative-change-style arrow-down': summaryData['totalVAOriginatedConversationsCompare'] < 0,
                }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalVAOriginatedConversationsCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description"></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
        <div class="breakdown-box breakdown-subbox-bottom live-agent-only-conversations">
          <div class="header">
            <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalLiveAgentOnlyConversations') }">Live Agent Only Conversations</div>
            <el-tooltip
              effect="dark"
              :class="{ disabled: !hasBreakDownData('totalLiveAgentOnlyConversations') }"
              :disabled="!hasBreakDownData('totalLiveAgentOnlyConversations')"
              popper-class="wy-page-tooltip-popup"
              placement="bottom"
            >
              <div slot="content"><span v-html="toolTipsContents.totalLiveAgentOnlyConversations"></span></div>
              <img class="info-icon" src="../../assets/icons/icon_info.svg" />
            </el-tooltip>
          </div>
          <div v-if="hasBreakDownData('totalLiveAgentOnlyConversations')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalLiveAgentOnlyConversations'] }}</span>
              <span v-if="summaryData['totalLiveAgentOnlyConversationsPercent'] > 99 && summaryData['totalLiveAgentOnlyConversationsPercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalLiveAgentOnlyConversationsPercent'] > 0 && summaryData['totalLiveAgentOnlyConversationsPercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalLiveAgentOnlyConversationsPercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalLiveAgentOnlyConversationsCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalLiveAgentOnlyConversationsCompare'] != 0"
                :class="{
                  'positive-change-style arrow-up': summaryData['totalLiveAgentOnlyConversationsCompare'] > 0,
                  'negative-change-style arrow-down': summaryData['totalLiveAgentOnlyConversationsCompare'] < 0,
                }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalLiveAgentOnlyConversationsCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description"></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
      </div>
      <div class="breakdown-va-only-breakdown">
        <div class="breakdown-box breakdown-subbox-top va-engaged-conversations">
          <div class="header">
            <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalVAEngagedConversations') }">VA Engaged Conversations</div>
            <el-tooltip
              effect="dark"
              :class="{ disabled: !hasBreakDownData('totalVAEngagedConversations') }"
              :disabled="!hasBreakDownData('totalVAEngagedConversations')"
              popper-class="wy-page-tooltip-popup"
              placement="bottom"
            >
              <div slot="content"><span v-html="toolTipsContents.totalVAEngagedConversations"></span></div>
              <img class="info-icon" src="../../assets/icons/icon_info.svg" />
            </el-tooltip>
          </div>
          <div v-if="hasBreakDownData('totalVAEngagedConversations')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalVAEngagedConversations'] }}</span>
              <span v-if="summaryData['totalVAEngagedConversationsPercent'] > 99 && summaryData['totalVAEngagedConversationsPercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalVAEngagedConversationsPercent'] > 0 && summaryData['totalVAEngagedConversationsPercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalVAEngagedConversationsPercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalVAEngagedConversationsCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalVAEngagedConversationsCompare'] != 0"
                :class="{
                  'positive-change-style arrow-up': summaryData['totalVAEngagedConversationsCompare'] > 0,
                  'negative-change-style arrow-down': summaryData['totalVAEngagedConversationsCompare'] < 0,
                }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalVAEngagedConversationsCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description"></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
        <div class="breakdown-box breakdown-subbox-bottom va-conversations-with-immediate-escalations">
          <div class="header">
            <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalVAConversationsWithImmedidate') }">VA Conversations with Immediate Escalations</div>
            <el-tooltip
              effect="dark"
              :class="{ disabled: !hasBreakDownData('totalVAConversationsWithImmedidate') }"
              :disabled="!hasBreakDownData('totalVAConversationsWithImmedidate')"
              popper-class="wy-page-tooltip-popup"
              placement="bottom"
            >
              <div slot="content"><span v-html="toolTipsContents.totalVAConversationsWithImmediateEscalations"></span></div>
              <img class="info-icon" src="../../assets/icons/icon_info.svg" />
            </el-tooltip>
          </div>
          <div v-if="hasBreakDownData('totalVAConversationsWithImmedidate')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalVAConversationsWithImmedidate'] }}</span>
              <span v-if="summaryData['totalVAConversationsWithImmedidatePercent'] > 99 && summaryData['totalVAConversationsWithImmedidatePercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalVAConversationsWithImmedidatePercent'] > 0 && summaryData['totalVAConversationsWithImmedidatePercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalVAConversationsWithImmedidatePercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalVAConversationsWithImmedidateCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalVAConversationsWithImmedidateCompare'] != 0"
                :class="{
                  'positive-change-style arrow-up': summaryData['totalVAConversationsWithImmedidateCompare'] > 0,
                  'negative-change-style arrow-down': summaryData['totalVAConversationsWithImmedidateCompare'] < 0,
                }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalVAConversationsWithImmedidateCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description"></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
      </div>
      <div class="breakdown-va-only-breakdown panel-3rd">
        <div class="breakdown-box breakdown-subbox-top va-contained-conversations">
          <div class="header">
            <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalVAContainedConversations') }">VA Contained Conversations</div>
            <el-tooltip
              effect="dark"
              :class="{ disabled: !hasBreakDownData('totalVAContainedConversations') }"
              :disabled="!hasBreakDownData('totalVAContainedConversations')"
              popper-class="wy-page-tooltip-popup"
              placement="bottom"
            >
              <div slot="content"><span v-html="toolTipsContents.totalVAContainedConversations"></span></div>
              <img class="info-icon" src="../../assets/icons/icon_info.svg" />
            </el-tooltip>
          </div>
          <div v-if="hasBreakDownData('totalVAContainedConversations')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalVAContainedConversations'] }}</span>
              <span v-if="summaryData['totalVAContainedConversationsPercent'] > 99 && summaryData['totalVAContainedConversationsPercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalVAContainedConversationsPercent'] > 0 && summaryData['totalVAContainedConversationsPercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalVAContainedConversationsPercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalVAContainedConversationsCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalVAContainedConversationsCompare'] != 0"
                :class="{
                  'positive-change-style arrow-up': summaryData['totalVAContainedConversationsCompare'] > 0,
                  'negative-change-style arrow-down': summaryData['totalVAContainedConversationsCompare'] < 0,
                }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalVAContainedConversationsCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description"></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
        <div class="breakdown-box breakdown-subbox-bottom va-engaged-with-live-agent-not-connected">
          <div class="header">
            <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalVAEngagedWithLiveAgentRequested') }">VA Engaged with Live Agent Requested but not Connected</div>
            <el-tooltip
              effect="dark"
              :class="{ disabled: !hasBreakDownData('totalVAEngagedWithLiveAgentRequested') }"
              :disabled="!hasBreakDownData('totalVAEngagedWithLiveAgentRequested')"
              popper-class="wy-page-tooltip-popup"
              placement="bottom"
            >
              <div slot="content"><span v-html="toolTipsContents.totalVAEngagedWithLiveAgentRequestedNotConnected"></span></div>
              <img class="info-icon" src="../../assets/icons/icon_info.svg" />
            </el-tooltip>
          </div>
          <div v-if="hasBreakDownData('totalVAEngagedWithLiveAgentRequested')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalVAEngagedWithLiveAgentRequested'] }}</span>
              <span v-if="summaryData['totalVAEngagedWithLiveAgentRequestedPercent'] > 99 && summaryData['totalVAEngagedWithLiveAgentRequestedPercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalVAEngagedWithLiveAgentRequestedPercent'] > 0 && summaryData['totalVAEngagedWithLiveAgentRequestedPercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalVAEngagedWithLiveAgentRequestedPercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalVAEngagedWithLiveAgentRequestedCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalVAEngagedWithLiveAgentRequestedCompare'] != 0"
                :class="{
                  'positive-change-style arrow-up': summaryData['totalVAEngagedWithLiveAgentRequestedCompare'] > 0,
                  'negative-change-style arrow-down': summaryData['totalVAEngagedWithLiveAgentRequestedCompare'] < 0,
                }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalVAEngagedWithLiveAgentRequestedCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description"></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
        <div class="breakdown-box breakdown-subbox-bottom va-engaged-with-live-agent-connected">
          <div class="header">
            <div class="breakdown-title" :class="{ disabled: !hasBreakDownData('totalVAEngagedWithLiveAgentConnected') }">VA Engaged with Live Agent Connected Conversations</div>
            <el-tooltip
              effect="dark"
              :class="{ disabled: !hasBreakDownData('totalVAEngagedWithLiveAgentConnected') }"
              :disabled="!hasBreakDownData('totalVAEngagedWithLiveAgentConnected')"
              popper-class="wy-page-tooltip-popup"
              placement="bottom"
            >
              <div slot="content"><span v-html="toolTipsContents.totalVAEngagedWithLiveAgentConnectedConversation"></span></div>
              <img class="info-icon" src="../../assets/icons/icon_info.svg" />
            </el-tooltip>
          </div>
          <div v-if="hasBreakDownData('totalVAEngagedWithLiveAgentConnected')" class="content">
            <div class="breakdown-number">
              <span>{{ formattedData['totalVAEngagedWithLiveAgentConnected'] }}</span>
              <span v-if="summaryData['totalVAEngagedWithLiveAgentConnectedPercent'] > 99 && summaryData['totalVAEngagedWithLiveAgentConnectedPercent'] < 100"> (&#62;99%)</span>
              <span v-else-if="summaryData['totalVAEngagedWithLiveAgentConnectedPercent'] > 0 && summaryData['totalVAEngagedWithLiveAgentConnectedPercent'] < 1"> (&#60;1%)</span>
              <span v-else> ({{ summaryData['totalVAEngagedWithLiveAgentConnectedPercent'].toFixed(0) }}%)</span>
            </div>
            <div v-if="summaryData['totalVAEngagedWithLiveAgentConnectedCompare'] != null" class="breakdown-percentage">
              <div
                v-if="summaryData['totalVAEngagedWithLiveAgentConnectedCompare'] != 0"
                :class="{
                  'positive-change-style arrow-up': summaryData['totalVAEngagedWithLiveAgentConnectedCompare'] > 0,
                  'negative-change-style arrow-down': summaryData['totalVAEngagedWithLiveAgentConnectedCompare'] < 0,
                }"
              >
                <span class="positive-change">{{ Math.abs(summaryData['totalVAEngagedWithLiveAgentConnectedCompare']) }}</span>
                <span>%</span>
              </div>
            </div>
            <div v-else class="breakdown-percentage">
              <div class="invalid-data-style">
                <span>N/A</span>
              </div>
            </div>
            <div class="breakdown-description"></div>
          </div>
          <div v-else class="content-no-data">
            <div class="no-data-info">N/A</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import WyUtils from '../../library/wy-utils.js';

export default Vue.extend({
  name: 'wy-analytics-stats-breakdown',
  props: ['controller', 'data'],

  data() {
    return {
      toolTipsContents: {},
    };
  },

  watch: {
    'controller.selectedStat': function(newValue, oldValue) {
      this.controller.handleFilterChange();

      const eventInfo = {
        oldValue: this.statsTitles[oldValue],
        newValue: this.statsTitles[newValue],
      };
      this.$router.logAmplitudeEvent({ eventName: 'Conversation_Distribution_Page_Filter_Convo', eventInfo });
    },
  },

  computed: {
    ...mapGetters(['dataModel']),

    summaryData() {
      return this.data ? this.data.modelStats : null;
    },

    formattedData() {
      if (!this.summaryData || this.summaryData.length == 0) {
        // If summaryData is not ready yet, any property of formattedData will be '-'
        return new Proxy(
          {},
          {
            get() {
              return '-';
            },
          }
        );
      }

      // Return the same data with every value formatted using our formatter
      return Object.fromEntries(Object.entries(this.summaryData).map(([key, value]) => [key, WyUtils.formatterNumber(value * this.$store.getters.xnumber)]));
    },
  },

  methods: {
    ...mapMutations([]),
    ...mapActions(['applyFilterValue']),

    handleOpenContactReasons() {
      this.$router.logAmplitudeEvent({ eventName: 'Conversation_Distribution_Page_View_Topics_Click', eventInfo: {} });
      this.$router.push({ path: '/analytics/contactReasonsPage' });
    },

    hasBreakDownData(name) {
      return this.formattedData[name] && this.formattedData[name] != '-1';
    },

    prepareTooltips() {
      this.toolTipsContents.totalConversation = 'Conversations in which at least one customer message is received (either typed or spoken).<br><br>';
      this.toolTipsContents.totalConversation = this.toolTipsContents.totalConversation + 'From the total sessions, inactive sessions are removed to capture Total Conversations.<br><br>';
      this.toolTipsContents.totalConversation =
        this.toolTipsContents.totalConversation +
        'Total Sessions are the number of times a chat window is triggered open or a voice call is attempted between the customer and agent (could be a virtual agent or live agent), whether initiated by the customer or agent. An inactive session is when no customer message is received.</b>';

      this.toolTipsContents.totalConversationsWithAConversationTopic = 'Conversations that contain a conversation topic';

      this.toolTipsContents.totalVAOriginatedConversations = "Conversations in which the virtual agent is the customer's first point of contact.";
      this.toolTipsContents.totalLiveAgentOnlyConversations = 'Conversations in which customers directly interact with a live agent, without involving the virtual agent.';
      this.toolTipsContents.totalVAEngagedConversations = 'Conversations with a virtual agent in which at least one customer message is received, which does not lead to immediate escalation.';
      this.toolTipsContents.totalVAConversationsWithImmediateEscalations =
        'Conversations in which customers begin interaction with a virtual agent and immediately get escalated (either because the customer requested for escalation or the journey is designed to escalate).';
      this.toolTipsContents.totalVAContainedConversations = 'Conversations in which only the virtual agent is engaged, without a Escalation taking place.';
      this.toolTipsContents.totalVAEngagedWithLiveAgentRequestedNotConnected = 'Conversations with a virtual agent in which escalation is requested but the customer is not connected to a live agent.';
      this.toolTipsContents.totalVAEngagedWithLiveAgentConnectedConversation = 'Conversations with a virtual agent that end with the customer escalating to a live agent.';
    },
  },

  created() {
    this.statsTitles = {
      conversations: 'Total Conversations',
      understood: 'Conversations with a topic',
    };
    this.prepareTooltips();
  },

  mounted() {
    this.$root.registerTooltips(this.$el);

    this.$root.$on(`fire-container-main-scroll-events`, () => {
      if (this.$refs.agentHandlingDropdown) {
        this.$refs.agentHandlingDropdown.blur();
      }
    });
  },

  beforeDestroy() {
    this.$root.unregisterTooltips(this.$el);
  },
});
</script>

<style lang="scss">
.wy-analytics-stats-breakdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: fit-content;
  padding-right: 20px;
  padding-bottom: 20px;

  .header {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0;
    width: 100%;
    height: 32px;

    .title {
      font-size: 19px;
      font-weight: bold;
    }
    .filter-label {
      margin-left: auto;
      margin-right: 5px;
      font-size: 14px;
    }
    .over-all-content {
      font-size: 16px;
      font-weight: 400;
      color: #6a767c;
    }

    .el-select {
      width: 205px;
    }
    .el-select:hover .el-input__inner {
      border-color: var(--site-font-primary-color);
    }
    .el-input.is-focus .el-input__inner {
      border-color: var(--site-font-primary-color);
    }
    .el-select .el-input__inner:focus {
      border-color: var(--site-font-primary-color);
    }
  }

  .content {
    .breakdown-percentage {
      margin-top: 10px;
      .positive-change-style.arrow-up {
        background-color: #f4f4fc;
        border: 1px solid #f4f4fc;
        color: #6446bb;
        padding-left: 24px !important;
        padding-right: 15px !important;
        font-weight: 400;
        border-radius: 30px;
        line-height: 34px;
        text-align: right;
        display: flex;
        font-size: 14.57px;
        height: 30px;
        width: fit-content;
        align-items: center;
        span {
          color: #4448a2;
        }
      }
      .negative-change-style.arrow-down {
        background-color: #effcff;
        border: 1px solid #effcff;
        color: #3961ae;
        padding-left: 24px !important;
        padding-right: 15px !important;
        border-radius: 30px;
        font-weight: 400;
        line-height: 34px;
        text-align: right;
        display: flex;
        align-items: center;
        font-size: 14.57px;
        height: 30px;
        width: fit-content;
        span {
          color: #3961ae;
        }
      }
      .arrow-up {
        background: url('../../assets/icons/icon_performance_arrow_up.svg') no-repeat;
        background-position-y: 7.87px;
        background-position-x: 10px;
        background-size: 11.95px;
      }
      .arrow-down {
        background: url('../../assets/icons/icon_performance_arrow_down.svg') no-repeat;
        background-position-y: 7.87px;
        background-position-x: 10px;
        background-size: 11.95px;
      }
      .positive-change {
        margin-left: 4px;
      }
      .invalid-data-style {
        background-color: #e4e8ec;
        border: 1px solid #effcff;
        color: var(--site-font-tertiary-color);
        padding-left: 30px !important;
        padding-right: 30px !important;
        border-radius: 30px;
        font-weight: 400;
        line-height: 34px;
        text-align: right;
        display: flex;
        align-items: center;
        font-size: 14.57px;
        height: 26px;
        width: fit-content;
        span {
          color: var(--site-font-tertiary-color);
        }
      }
    }
    .breakdown-number {
      margin: 5px 5px 5px 0px;
      font-weight: 900;
      font-size: 25px;
      color: var(--site-font-tertiary-color);
      // text-align: center;
    }
    .breakdown-description {
      font-size: 14.22px;
      font-weight: 400;
      color: var(--site-font-tertiary-color);
      bottom: -20px;
      position: relative;
      width: 189px;
      .view-topics {
        color: #1a55ad;
        cursor: pointer;
      }
    }
  }
  .content-no-data {
    padding-top: 15px;
    .no-data-info {
      color: #cad2da;
      font-size: 25px;
      font-weight: 500;
    }
  }

  .breakdown-title {
    margin: 0px;
    font-weight: 500;
    font-size: 14.5px;
    color: #6a767c;
    text-align: center;
    margin-right: auto;
  }
  .breakdown-title.disabled {
    color: #cad2da;
  }

  .breakdown-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 30px;
    width: 100%;

    .title {
      font-size: 19px;
      font-weight: bold;
    }
    .filter-label {
      margin-left: auto;
      margin-right: 5px;
      font-size: 14px;
    }
    .over-all-content {
      font-size: 16px;
      font-weight: 400;
      color: #6a767c;
    }

    .el-select {
      width: 205px;
    }
    .el-select:hover .el-input__inner {
      border-color: var(--site-font-primary-color);
    }
    .el-input.is-focus .el-input__inner {
      border-color: var(--site-font-primary-color);
    }
    .el-select .el-input__inner:focus {
      border-color: var(--site-font-primary-color);
    }
    .el-select > .el-input {
      // display: block;
      border: 1px solid #cad2da;
      border-radius: 5px;
      .el-input__inner {
        background-color: transparent;
        border-width: 0px !important;
        height: 31px;
        padding-left: 10px;
      }
      .el-input__icon {
        line-height: 30px;
      }
    }
    .el-input.el-input--suffix.is-focus {
      border: 1px solid #106fbc !important;
    }
    .el-select > .el-input:hover {
      background-color: #f4f6f8;
    }
  }
  .breakdown-body {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    margin-top: 50px;

    // &::before {
    //   content: "";
    //   position: absolute;
    //   width: 37%;
    //   //min-width: 200px;
    //   height: 23px;
    //   right: 50%;
    //   top: -24px;
    //   border-right: 2px solid rgb(206, 216, 228);
    //   border-bottom: 2px solid rgb(206, 216, 228);
    //   border-bottom-right-radius: 38px;
    // }
    // &::after {
    //   content: "";
    //   position: absolute;
    //   width: 37%;
    //   //min-width: 400px;
    //   height: 23px;
    //   left: 50%;
    //   top: -24px;
    //   border-left: 2px solid rgb(206, 216, 228);
    //   border-bottom: 2px solid rgb(206, 216, 228);
    //   border-bottom-left-radius: 38px;
    // }

    .breakdown-box {
      position: relative;
      display: flex;
      flex-direction: column;
      // justify-content: center;
      // align-items: center;

      width: calc(23% - 0px);
      height: 150px;
      padding: 0px 10px;
      box-shadow: 0px 0px 10px rgba(86, 124, 170, 0.22);
      border-radius: 9px;
      min-width: 197px;

      &::before {
        content: '';
        position: absolute;
        width: calc(13px + 50px);
        height: 25px; // 24px of margin + 1px of border
        right: 50%;
        bottom: 100%;
        border-right: 2px solid rgb(206, 216, 228);
        border-top: 2px solid rgb(206, 216, 228);
        border-top-right-radius: 38px;
        box-sizing: border-box;
      }

      .el-icon-info {
        position: relative;
        top: 0px;
        align-self: flex-end;
        color: #214586;
        line-height: 2;
      }
    }

    .breakdown-box.va-originated-conversations {
      .breakdown-title {
        max-width: 275px;
        min-width: 208px;
      }
    }
    .breakdown-box.live-agent-only-conversations {
      .breakdown-title {
        max-width: 275px;
        min-width: 208px;
      }
    }
    .breakdown-box.va-originated-conversations {
      height: 150px;
    }
    .breakdown-box.va-engaged-with-live-agent-not-connected {
      height: 150px;
      .header {
        height: 44px;
      }
      .breakdown-title {
        padding-top: 10px;
        max-width: 275px;
        min-width: 205px;
      }
      .info-icon {
        top: -6px;
        cursor: pointer;
      }
      .content .breakdown-number {
        margin: 10px 5px 10px 0px;
      }
    }
    .breakdown-box.va-engaged-with-live-agent-connected {
      .header {
        height: 44px;
      }
      .breakdown-title {
        padding-top: 10px;
        max-width: 275px;
        min-width: 204px;
      }
      .info-icon {
        top: -6px;
        cursor: pointer;
      }
      .content .breakdown-number {
        margin: 10px 5px 10px 0px;
      }
    }
    .breakdown-box.va-conversations-with-immediate-escalations {
      .header {
        height: 44px;
      }
      .breakdown-title {
        padding-top: 10px;
        max-width: 275px;
        min-width: 204px;
      }
      .info-icon {
        top: -6px;
        cursor: pointer;
      }
      .content .breakdown-number {
        margin: 10px 5px 10px 0px;
      }
    }

    .breakdown-box-va-only {
      position: relative;
      margin-right: 0;

      &::before {
        left: 50%;
        border-left: 2px solid rgb(206, 216, 228);
        border-right: none;
        border-top-left-radius: 38px;
        border-top-right-radius: 0;
      }
    }
    .breakdown-box.breakdown-box-all-conversation {
      &::before {
        display: none;
      }
    }
    .breakdown-box-all-conversation {
      position: relative;
      margin-right: 0;
      height: 175px;
      min-width: 245px;

      // &::before {
      //   left: 50%;
      //   border-left: 2px solid rgb(206, 216, 228);
      //   border-right: none;
      //   border-top-left-radius: 38px;
      //   border-top-right-radius: 0;
      // }
    }

    .breakdown-va-only-breakdown {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      grid-gap: 50px;

      width: calc(25%);
      height: 100%;
      margin-left: 68px;
      margin-right: 0px;

      &::before {
        // content: "";
        // position: absolute;
        // width: 41px;
        // height: calc(25% - 20px);
        // right: calc(100% + 33px - 7px - 1px);
        // bottom: 50%;
        // border-bottom: 2px solid rgb(206, 216, 228);
        // border-right: 2px solid rgb(206, 216, 228);
        // border-bottom-right-radius: 38px;
      }
      &::after {
        content: '';
        position: absolute;
        width: 35px;
        height: calc(57%);
        right: calc(100% + 31px);
        top: 16%;
        border-top: 2px solid rgb(206, 216, 228);
        border-right: 2px solid rgb(206, 216, 228);
        border-top-right-radius: 38px;
      }

      .breakdown-number {
        font-size: 25px;
      }
      .breakdown-title {
        font-size: 14.5px;
        text-align: start;
      }

      .breakdown-box {
        // display: flex;
        // flex-direction: column;
        // justify-content: center;
        // align-items: flex-start;
        // flex-basis: 85px;
        // flex-grow: 1;
        // flex-shrink: 1;

        width: calc(100% - 20px);
        // height: calc(50% - 3px);
      }

      .breakdown-subbox-top {
        margin-bottom: auto;
        &::before {
          // width: 33px;  // 34px of margin - 1px of border
          width: 63px;
          height: 1px;
          right: 100%;
          top: 38%;
          border-top: 2px solid rgb(206, 216, 228);
          border-left: 2px solid rgb(206, 216, 228);
          border-right: 0;
          border-top-left-radius: 38px;
          border-top-right-radius: 0;
        }
      }

      .breakdown-subbox-bottom {
        margin-top: auto;
        &::before {
          width: 33px; // 34px of margin - 1px of border
          height: 12.5%;
          right: 100%;
          bottom: 50%;
          border-bottom: 2px solid rgb(206, 216, 228);
          border-top: none;
          border-left: 2px solid rgb(206, 216, 228);
          border-right: 0;
          border-bottom-left-radius: 38px;
          border-top-left-radius: 0;
        }
      }
      .breakdown-subbox-bottom.va-engaged-with-live-agent {
        margin-top: auto;
        &::before {
          width: 33px; // 34px of margin - 1px of border
          height: 12.5%;
          right: 100%;
          bottom: 50%;
          border-bottom: 2px solid rgb(206, 216, 228);
          border-top: none;
          border-left: 2px solid rgb(206, 216, 228);
          border-right: 0;
          border-bottom-left-radius: 38px;
          border-top-left-radius: 0;
        }
        &::after {
          content: '';
          position: absolute;
          width: 48px;
          height: calc(65% - 20px);
          right: calc(100% + 33px - 7px - 1px);
          top: 18%;
          border-top: 2px solid rgb(206, 216, 228);
          border-right: 2px solid rgb(206, 216, 228);
          border-top-right-radius: 38px;
        }
      }
    }
    .breakdown-va-only-breakdown.panel-3rd {
      &::after {
        content: '';
        position: absolute;
        width: 35px;
        height: calc(73%);
        right: calc(100% + 31px);
        top: calc(11% - 5px);
        border-top: 2px solid rgb(206, 216, 228);
        border-right: 2px solid rgb(206, 216, 228);
        border-top-right-radius: 38px;
      }
    }

    .info-icon {
      // margin-top: 2px;
      width: 17px;
      position: relative;
      left: 3px;
      cursor: pointer;
      // top: 3px;
    }
    .el-tooltip.info-icon.disabled {
      opacity: 0.5;
      cursor: default;
    }
  }

  .stats-summary-details {
    width: 100%;
    padding-bottom: 30px;
    padding-top: 60px;

    .stats-summary-details-table {
      width: 100%;
      td {
        text-align: center;
        width: 25%;
      }
    }
  }

  .stats-summary-trends {
    width: 100%;
  }

  .user-select-none.svg-container {
    background-color: transparent;
    .main-svg {
      background-color: transparent !important;
      .bglayer {
        rect {
          fill: transparent !important;
        }
      }
    }
    .legendtext {
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    }
    .xtick {
      text {
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
      }
    }
  }

  .no-data-available {
    text-align: center;
    padding-bottom: 30px;
  }
}
.overall-breakdown {
  .el-select-dropdown__item.selected {
    // color: var(--site-font-drop-down-secondary-color);
    // font-weight: unset;
    background-color: #e8f1ff !important;
    span {
      color: #1a55ad;
      font-weight: 500;
    }
  }
  .el-select-dropdown__item.hover {
    background-color: unset;
  }
  .el-select-dropdown__item:hover {
    background-color: #f5f7fa;
  }
}
</style>
